import { useState, useContext, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import ChatInput from '@/components/ChatInput';
import LoginDialog from '@/components/LoginDialog';
import UserContext from '@/components/UserContext';
import SessionHeader from '@/components/session/SessionHeader';
import MessageList from '@/components/session/MessageList';
import EmptyState from '@/components/session/EmptyState';
import WelcomeMessage from '@/components/session/WelcomeMessage';
import { supabase } from '@/services/auth';
import { cn } from '@/utils';
import { Sun, Moon, Monitor } from 'lucide-react';
import { Model } from '@/components/ModelDropdown';
import { getCredits } from '@/services/credits';
import { useTheme } from '@/contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  image?: string;
  imageId?: string;
  previewImage?: string;
  isGeneratingImage?: boolean;
  imageGenerationStartTime?: number;
  model?: string;
  userPrompt?: string;
  promptId?: string;
  rating?: 'thumbs_up' | 'thumbs_down' | null;
  error?: string;
  hasError?: boolean;
}

interface SessionContainerProps {
  sessionId?: string;
  sessionTitle?: string;
  sessionSlug?: string;
  messages: Message[];
  isLoading?: boolean;
  isCreatingSession?: boolean;
  isStreaming?: boolean;
  onSubmit?: (prompt: string) => void;
  onRegenerateWithModel?: (prompt: string, modelId: string, promptId?: string, modelObject?: any) => void;
  sidebarOpen: boolean;
  onSidebarToggle: () => void;
  hasLoaded: boolean;
  selectedModel?: Model;
  onModelChange?: (model: Model) => void;
  sessionKey?: string;
}

export default function SessionContainer({
  sessionId,
  sessionTitle = 'Untitled Chat',
  sessionSlug,
  messages,
  isLoading = false,
  isCreatingSession = false,
  isStreaming = false,
  onSubmit,
  onRegenerateWithModel,
  sidebarOpen,
  onSidebarToggle,
  hasLoaded,
  selectedModel,
  onModelChange,
  sessionKey,
}: SessionContainerProps) {
  const [prompt, setPrompt] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user, userDetails } = useContext(UserContext);
  const { themeMode, setThemeMode } = useTheme();

  const getThemeIcon = () => {
    switch (themeMode) {
      case 'light':
        return <Sun className="h-5 w-5" />;
      case 'dark':
        return <Moon className="h-5 w-5" />;
      case 'system':
        return <Monitor className="h-5 w-5" />;
    }
  };

  // Auto-scroll to bottom with smooth animation
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Instant scroll to bottom without animation (for session loading)
  const scrollToBottomInstant = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
    }
  };

  // Check if user is near bottom of scroll area
  const isNearBottom = () => {
    const scrollElement = document.querySelector('.flex-1.overflow-y-auto');
    if (!scrollElement) return false;

    const { scrollTop, scrollHeight, clientHeight } = scrollElement;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom < 200;
  };

  // Track previous image generation state
  const prevGeneratingRef = useRef<boolean>(false);

  // Scroll to bottom instantly when session changes
  useEffect(() => {
    if (sessionKey && messages.length > 0) {
      // Use a small delay to ensure DOM is updated
      // make sure no anchor is in the url
      if (!window.location.hash) {
        console.log('scrollToBottomInstant');
        setTimeout(() => scrollToBottomInstant(), 50);
      }
    }
  }, [sessionKey]);

  // Auto-scroll when important events occur
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    const wasNearBottom = isNearBottom();

    // Check if image generation just started
    const imageJustStarted = lastMessage.isGeneratingImage && !prevGeneratingRef.current;
    prevGeneratingRef.current = lastMessage.isGeneratingImage || false;

    // Conditions for auto-scrolling:
    // 1. User sends a message (always scroll for user messages)
    // 2. Image generation just started (always scroll when generation begins)
    // 3. When near bottom AND:
    //    - Last message is from assistant (new response or update)
    //    - Image just completed (imageId was added)
    const shouldScroll =
      lastMessage.role === 'user' ||
      imageJustStarted ||
      (wasNearBottom &&
        (lastMessage.role === 'assistant' ||
          (lastMessage.imageId && !lastMessage.isGeneratingImage)));

    if (shouldScroll) {
      // Small delay to ensure DOM updates
      if (!window.location.hash) {
        setTimeout(() => scrollToBottom(), 100);
      }
    }
  }, [messages]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!prompt.trim()) return;

    if (!user) {
      setShowLogin(true);
      return;
    }

    const currentPrompt = prompt;
    setPrompt(''); // Clear input immediately

    // Scroll to bottom when submitting new message
    setTimeout(() => scrollToBottom(), 100);

    // Call the parent's onSubmit handler
    onSubmit?.(currentPrompt);
  };

  return (
    <>
      <div className="flex-1 flex flex-col relative bg-white dark:bg-gray-900 min-w-0">
        {/* Logo in top left when sidebar is closed */}
        <WelcomeMessage
          sidebarOpen={sidebarOpen}
          sessionId={sessionId}
          onSidebarToggle={onSidebarToggle}
        />

        {/* Fixed header with session title - responsive behavior */}
        <SessionHeader
          sessionId={sessionId}
          sessionTitle={sessionTitle}
          sessionSlug={sessionSlug}
          sidebarOpen={sidebarOpen}
          onSidebarToggle={onSidebarToggle}
          hasLoaded={hasLoaded}
          isLoading={isLoading}
        />

        {/* Main content area - Messages */}
        <div
          className={cn(
            'flex-1 overflow-y-auto relative z-10',
            // Only add padding on narrow screens where header is solid
            sessionId && 'lg:pt-0 pt-[76px]'
          )}
        >
          <div className="max-w-3xl lg:max-w-4xl xl:max-w-5xl mx-auto px-4 py-6 lg:py-24 lg:pb-32">
            {messages.length === 0 && !isCreatingSession && !isLoading ? (
              <EmptyState isLoading={isLoading} />
            ) : (
              <>
                <MessageList
                  messages={messages}
                  sessionTitle={sessionTitle}
                  sessionId={sessionId}
                  onRegenerate={onRegenerateWithModel}
                  isStreaming={isStreaming}
                  isCreatingSession={isCreatingSession}
                  isLoading={isLoading}
                />

                {/* Invisible div to scroll to */}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
        </div>

        {/* Chat input at bottom - positioned within container */}
        <div className="sticky bottom-0 z-20 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm border-t border-gray-200/50 dark:border-gray-700/50">
          <div className="max-w-3xl lg:max-w-4xl xl:max-w-5xl mx-auto px-4 pb-6 pt-3">
            <ChatInput
              className="shadow-md shadow-gray-700/10 border-black/30 border-[0.5px]"
              prompt={prompt}
              onPromptChange={setPrompt}
              onSubmit={handleSubmit}
              isLoading={isStreaming}
              placeholder="How can I help you?"
              selectedModel={selectedModel}
              onModelChange={onModelChange}
              userCreditsRemaining={userDetails ? getCredits(userDetails).remaining : undefined}
            />
          </div>
        </div>
      </div>

      {/* Floating Theme Selector for logged-out users */}
      {!user && (
        <div className="fixed bottom-24 right-6 z-50">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-12 w-12 rounded-full shadow-lg bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600"
                aria-label="Change theme"
              >
                {getThemeIcon()}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top">
              <DropdownMenuItem onClick={() => setThemeMode('light')}>
                <Sun className="h-4 w-4 mr-2" />
                Light
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setThemeMode('dark')}>
                <Moon className="h-4 w-4 mr-2" />
                Dark
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setThemeMode('system')}>
                <Monitor className="h-4 w-4 mr-2" />
                System
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {!user && (
        <LoginDialog
          supabase={supabase}
          open={showLogin}
          isSignIn={false}
          onSignedIn={() => setShowLogin(false)}
          onClose={() => setShowLogin(false)}
        />
      )}
    </>
  );
}